import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import AppRoutes from "./routes/routes";
import { postMessage } from "./lib/post-message";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./redux/store";
import Toaster from "./components/template/Toaster/toaster";
import { hideToaster } from "./redux/slices/toasterSlice";
import { checkToken } from "./redux/slices/authSlice";
import { ToastContainer } from "react-toastify";

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { message, type } = useSelector((state: RootState) => state.toaster);
  const auth = useSelector((state: RootState) => state.auth);

  const handleToasterClose = () => {
    dispatch(hideToaster());
  };

  // Set user context and start page transaction
  useEffect(() => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const pageName =
      pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "Home";

    // Set user context in Sentry
    if (auth?.userId || auth?.tenant) {
      Sentry.setUser({
        id: auth.userId || "",
        email: auth.userName || "",
        username: auth.userName || "",
      });

      // Set custom context for user info
      Sentry.setContext("user_info", {
        userId: auth.userId,
        tenant: auth.tenant,
        username: auth.userName,
      });

      // Set tags for better filtering
      Sentry.setTag("tenant", auth.tenant || "");
      Sentry.setTag("userId", auth.userId || "");
      Sentry.setTag("currentPage", pageName);
    }

    // Add breadcrumb for page navigation
    Sentry.addBreadcrumb({
      message: `Navigated to ${pageName}`,
      category: "navigation",
      level: "info",
      data: {
        path: window.location.pathname,
        page: pageName,
      },
    });

    const handleLoad = () => {
      // Add breadcrumb for completed load
      Sentry.addBreadcrumb({
        message: `Page ${pageName} loaded successfully`,
        category: "performance",
        level: "info",
        data: {
          loadTime: performance.now(),
          page: pageName,
        },
      });
    };

    window.addEventListener("load", handleLoad);

    return () => {
      window.removeEventListener("load", handleLoad);
      
    };
  }, [auth]);

  // Token checking with error handling
  useEffect(() => {
    const handleCheckToken = async () => {
      try {
        console.log("Checking token");

        // Add breadcrumb for token check
        Sentry.addBreadcrumb({
          message: "Checking authentication token",
          category: "auth",
          level: "info",
          data: {
            userId: auth?.userId || "anonymous",
            tenant: auth?.tenant || "unknown",
          },
        });

        await dispatch(checkToken());

        // Success breadcrumb
        Sentry.addBreadcrumb({
          message: "Token check completed successfully",
          category: "auth",
          level: "info",
        });
      } catch (error) {
        // Set user context for error reporting
        if (auth?.userId || auth?.tenant) {
          Sentry.setUser({
            id: auth.userId || "",
            email: auth.userName || "",
            username: auth.userName || "",
          });

          Sentry.setContext("user_info", {
            userId: auth.userId,
            tenant: auth.tenant,
            username: auth.userName,
          });
        }

        // Capture the error
        Sentry.captureException(error, {
          tags: {
            source: "token_check",
            userId: auth?.userId || "",
            tenant: auth?.tenant || "",
          },
          contexts: {
            auth_check: {
              action: "periodic_token_check",
              timestamp: new Date().toISOString(),
            },
          },
        });

        console.error("Token check failed:", error);
      }
    };

    handleCheckToken();

    const interval = setInterval(() => {
      console.log("Checking token in interval");
      handleCheckToken();
    }, 600000); // 10 minutes

    return () => clearInterval(interval);
  }, [dispatch, auth]);

  // Service worker and network status monitoring
  useEffect(() => {
    navigator.serviceWorker.ready
      .then(() => {
        console.log("Service worker is ready. Syncing queue...");

        // Add breadcrumb for service worker ready
        Sentry.addBreadcrumb({
          message: "Service worker ready, syncing queue",
          category: "sw",
          level: "info",
          data: {
            userId: auth?.userId || "anonymous",
            tenant: auth?.tenant || "unknown",
          },
        });

        postMessage({
          type: "SYNC_QUEUE",
          payload: {
            message: "Sync queue",
          },
        });
      })
      .catch((error) => {
        console.error(
          "Service worker not registered or failed to become ready:",
          error
        );

        // Set user context for error reporting
        if (auth?.userId || auth?.tenant) {
          Sentry.setUser({
            id: auth?.userId || "",
            email: auth?.userName || "",
            username: auth.userName || "",
          });

          Sentry.setContext("user_info", {
            userId: auth.userId,
            tenant: auth.tenant,
            username: auth.userName,
          });

          Sentry.setTag("tenant", auth.tenant || "");
          Sentry.setTag("userId", auth.userId || "");
        }

        // Capture the service worker error
        Sentry.captureException(error, {
          tags: {
            source: "service_worker_ready",
            userId: auth?.userId || "",
            tenant: auth?.tenant || "",
          },
          contexts: {
            service_worker: {
              action: "ready_check",
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
            },
          },
        });
      });

    function handleOnline() {
      console.log("Online");

      // Set network status context
      Sentry.setContext("network_status", {
        status: "online",
        timestamp: new Date().toISOString(),
        id: auth?.userId,
        tenant: auth?.tenant,
        username: auth.userName,
      });

      // Add breadcrumb for network status change
      Sentry.addBreadcrumb({
        message: "Network status changed to online",
        category: "network",
        level: "info",
        data: {
          status: "online",
          userId: auth?.userId || "anonymous",
          tenant: auth?.tenant || "unknown",
          timestamp: new Date().toISOString(),
        },
      });

      // Set tag for current network status
      Sentry.setTag("networkStatus", "online");
    }

    function handleOffline() {
      console.log("Offline");

      // Set network status context
      Sentry.setContext("network_status", {
        status: "offline",
        timestamp: new Date().toISOString(),
        id: auth?.userId,
        tenant: auth?.tenant,
        username: auth.userName,
      });

      // Add breadcrumb for network status change
      Sentry.addBreadcrumb({
        message: "Network status changed to offline",
        category: "network",
        level: "warning",
        data: {
          status: "offline",
          userId: auth?.userId || "anonymous",
          tenant: auth?.tenant || "unknown",
          timestamp: new Date().toISOString(),
        },
      });

      // Set tag for current network status
      Sentry.setTag("networkStatus", "offline");

      // Optionally capture offline events as messages (not errors)
      Sentry.captureMessage("Application went offline", {
        level: "warning", // Specify the level explicitly
        tags: {
          source: "network_status",
          userId: auth?.userId ?? "", // Use nullish coalescing for safer access
          tenant: auth?.tenant ?? "",
        },
        contexts: {
          network_event: {
            status: "offline",
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          },
        },
      });
    }

    // Set initial network status
    const initialNetworkStatus = navigator.onLine ? "online" : "offline";
    Sentry.setTag("networkStatus", initialNetworkStatus);
    Sentry.setContext("network_status", {
      status: initialNetworkStatus,
      timestamp: new Date().toISOString(),
      id: auth?.userId,
      tenant: auth?.tenant,
      username: auth.userName,
    });

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [auth]);

  // Monitor route changes for better error context
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Add breadcrumb for route change
    Sentry.addBreadcrumb({
      message: `Route changed to ${currentPath}`,
      category: "navigation",
      level: "info",
      data: {
        from: document.referrer || "direct",
        to: currentPath,
        timestamp: new Date().toISOString(),
      },
    });

    // Set current route as tag
    Sentry.setTag("currentRoute", currentPath);
  }, [window.location.pathname]);

  return (
    <>
      <AppRoutes />
      {message && type && (
        <Toaster message={message} type={type} onClose={handleToasterClose} />
      )}
      <ToastContainer />
    </>
  );
};

// Wrap the App component with Sentry for additional React-specific error tracking
export default App;
